import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  associateBridgeIdM3,
  clearShareAbhaDataM3HealthRecords,
  clearShareAbhaDataM3PatientData,
  fetchConsentArtifactM3,
  getBridgeServicesM3,
  getConsentRequestStatusM3,
  getHealthRecordByIdM3,
  initiateConsentRequestM3,
  listConsentArtifactsM3,
  listConsentRequestsM3,
  listHealthRecordsM3,
  notifyHealthInformationM3,
  requestHealthInformationM3,
  setBridgeUrlM3,
} from "../../../../store/slices/shareAbhaDataM3Slice";
import styles from "./sections.module.scss";

const HI_TYPE_OPTIONS = [
  { value: "Prescription", label: "Prescription" },
  { value: "DiagnosticReport", label: "Diagnostic Report" },
  { value: "DischargeSummary", label: "Discharge Summary" },
  { value: "ImmunizationRecord", label: "Immunization Record" },
  { value: "HealthDocumentRecord", label: "Health Document" },
  { value: "WellnessRecord", label: "Wellness Record" },
  { value: "OPConsultation", label: "OP Consultation" },
  { value: "Invoice", label: "Invoice" },
];

const HI_TYPE_ALIASES = {
  DischargeSummary: ["DischargeSummary", "DischargeSummaryRecord"],
  OPConsultation: ["OPConsultation", "OPConsultRecord"],
  Invoice: ["Invoice", "InvoiceRecord"],
};

const M3_LIST_LIMIT = 100;

const toIsoStart = (value) =>
  value ? new Date(`${value}T00:00:00.000Z`).toISOString() : undefined;

const toIsoEnd = (value) => {
  if (!value) return undefined;
  const endOfSelectedDate = new Date(`${value}T23:59:59.999Z`);
  const now = new Date();
  return (endOfSelectedDate > now ? now : endOfSelectedDate).toISOString();
};

const toDateInputValue = (date) => date.toISOString().slice(0, 10);

const formatHiType = (value) =>
  `${value || ""}`
    .replace(/Record$/, "")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .trim() || "Health Record";

const formatDateTime = (value) => {
  if (!value) return "-";
  const parsedDate = new Date(value);
  if (Number.isNaN(parsedDate.getTime())) return "-";
  return parsedDate.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const getStatusClass = (status) => {
  const normalizedStatus = `${status || ""}`.toUpperCase();
  if (["GRANTED", "ACTIVE", "RECEIVED", "COMPLETED"].includes(normalizedStatus)) {
    return styles.success;
  }
  if (["REQUESTED", "PENDING"].includes(normalizedStatus)) {
    return styles.warning;
  }
  if (["DENIED", "FAILED", "EXPIRED", "REVOKED"].includes(normalizedStatus)) {
    return styles.danger;
  }
  return styles.neutral;
};

const normalizeConsentStatus = (status) => `${status || ""}`.toUpperCase();

const isConsentGrantedStatus = (status) =>
  ["GRANTED", "ACTIVE"].includes(normalizeConsentStatus(status));

const isConsentPendingStatus = (status) =>
  ["REQUESTED", "PENDING"].includes(normalizeConsentStatus(status));

const isConsentDeniedStatus = (status) =>
  ["DENIED", "REJECTED", "DECLINED"].includes(normalizeConsentStatus(status));

const isConsentBlockedStatus = (status) =>
  isConsentDeniedStatus(status) || ["REVOKED", "EXPIRED"].includes(normalizeConsentStatus(status));

const getConsentBlockMessage = (status) => {
  const normalizedStatus = normalizeConsentStatus(status);
  if (isConsentDeniedStatus(normalizedStatus)) {
    return "Patient declined this consent request. Send a new request if records are required.";
  }
  if (normalizedStatus === "REVOKED") {
    return "Patient revoked this consent. Send a fresh consent request to fetch records again.";
  }
  if (normalizedStatus === "EXPIRED") {
    return "Consent request expired. Send a new consent request to fetch records.";
  }
  return "";
};

const getConsentStatusInfo = (status, hasRequest, hasConsentId) => {
  const normalizedStatus = normalizeConsentStatus(status);
  if (isConsentGrantedStatus(normalizedStatus)) {
    return {
      summary: "Approved",
      requestText: "GRANTED",
      approvalText: "Approved in ABHA app",
      recordsText: hasConsentId ? "Ready to fetch records" : "Fetching consent artifact",
      alertClass: "alert-success",
      notice: "",
    };
  }
  if (isConsentPendingStatus(normalizedStatus)) {
    return {
      summary: "Awaiting approval",
      requestText: normalizedStatus,
      approvalText: "Waiting for patient action",
      recordsText: "Ready after consent approval",
      alertClass: "alert-info",
      notice: "Please ask the patient to approve, deny, revoke, or let the request expire in the ABHA sandbox app, then refresh the status.",
    };
  }
  if (isConsentBlockedStatus(normalizedStatus)) {
    return {
      summary: normalizedStatus === "REJECTED" || normalizedStatus === "DECLINED" ? "Denied" : normalizedStatus,
      requestText: isConsentDeniedStatus(normalizedStatus) ? "DENIED" : normalizedStatus,
      approvalText:
        normalizedStatus === "REVOKED"
          ? "Revoked by patient"
          : normalizedStatus === "EXPIRED"
          ? "Consent expired"
          : "Denied by patient",
      recordsText: "Records cannot be fetched",
      alertClass: "alert-warning",
      notice: getConsentBlockMessage(normalizedStatus),
    };
  }
  return {
    summary: hasRequest || hasConsentId ? "Awaiting approval" : "Not requested",
    requestText: hasRequest ? "Requested" : "Not sent yet",
    approvalText: "Waiting for patient action",
    recordsText: "Ready after consent approval",
    alertClass: "alert-info",
    notice: "",
  };
};

const findConsentRequestId = (source) => {
  if (!source || typeof source !== "object") return "";
  if (typeof source.consentRequestId === "string") return source.consentRequestId;
  if (typeof source.requestId === "string") return source.requestId;
  if (Array.isArray(source)) {
    for (const item of source) {
      const found = findConsentRequestId(item);
      if (found) return found;
    }
    return "";
  }
  for (const value of Object.values(source)) {
    const found = findConsentRequestId(value);
    if (found) return found;
  }
  return "";
};

const getItemTime = (item) => {
  const value =
    item?.createdAt ||
    item?.updatedAt ||
    item?.consentDetail?.createdAt ||
    item?.notification?.consentDetail?.createdAt ||
    item?.permission?.dateRange?.from;
  const parsed = value ? new Date(value).getTime() : 0;
  return Number.isNaN(parsed) ? 0 : parsed;
};

const newestFirst = (items) =>
  [...(items || [])].sort((first, second) => getItemTime(second) - getItemTime(first));

const sleep = (milliseconds) =>
  new Promise((resolve) => {
    setTimeout(resolve, milliseconds);
  });

const getMatchingHiTypes = (value) => HI_TYPE_ALIASES[value] || [value];

const AbhaM3Section = ({ patientData }) => {
  const dispatch = useDispatch();
  const { clinicInfo } = useSelector((state) => state.clinic || {});
  const { token, userInfo } = useSelector((state) => state.auth || {});
  const clinicId =
    clinicInfo?.[0]?.clinicId ||
    userInfo?.clinicInfo?.[0]?.clinicId ||
    sessionStorage.getItem("clinicId") ||
    "";
  const authToken = token || userInfo?.token || "";
  const {
    loading,
    error,
    lastMessage,
    consentRequests,
    consentArtifacts,
    healthRecords,
    healthRecordDetail,
    bridgeInfo,
    bridgeServices,
    bridgeServiceMatched,
    recommendedHiuServiceId,
  } = useSelector((state) => state.shareAbhaDataM3 || {});

  const [patientAbhaAddress, setPatientAbhaAddress] = useState("");
  const [hiuId, setHiuId] = useState("");
  const [hiType, setHiType] = useState("Prescription");
  const [dateFrom, setDateFrom] = useState(() =>
    toDateInputValue(new Date(Date.now() - 365 * 24 * 60 * 60 * 1000))
  );
  const [dateTo, setDateTo] = useState(() => toDateInputValue(new Date()));
  const [consentIdInput, setConsentIdInput] = useState("");
  const [notifyTransactionId, setNotifyTransactionId] = useState("");
  const [localMessage, setLocalMessage] = useState("");
  const [showCurrentRecords, setShowCurrentRecords] = useState(false);
  const [activeConsentRequestId, setActiveConsentRequestId] = useState("");
  const [consentRequestedAt, setConsentRequestedAt] = useState(0);

  useEffect(() => {
    const resolvedAddress =
      patientData?.userId?.abhaAddress ||
      patientData?.abhaAddress ||
      patientData?.ABHAAddress ||
      "";
    dispatch(clearShareAbhaDataM3PatientData());
    setPatientAbhaAddress(resolvedAddress);
    setConsentIdInput("");
    setNotifyTransactionId("");
    setShowCurrentRecords(false);
    setActiveConsentRequestId("");
    setConsentRequestedAt(0);
    setLocalMessage(
      resolvedAddress ? "" : "Patient ABHA address is not available. Link ABHA before requesting records."
    );
  }, [dispatch, patientData]);

  const itemHasSelectedHiType = useCallback((item) => {
    if (!Array.isArray(item?.hiTypes) || item.hiTypes.length === 0) return true;
    const matchingTypes = getMatchingHiTypes(hiType);
    return item.hiTypes.some((itemHiType) => matchingTypes.includes(itemHiType));
  }, [hiType]);

  const patientConsentRequests = useMemo(
    () => (patientAbhaAddress ? consentRequests || [] : []),
    [consentRequests, patientAbhaAddress]
  );
  const patientConsentArtifacts = useMemo(
    () => (patientAbhaAddress ? consentArtifacts || [] : []),
    [consentArtifacts, patientAbhaAddress]
  );
  const currentConsentRequest = useMemo(
    () => {
      const newestSelectedRequests = newestFirst(patientConsentRequests).filter((item) =>
        itemHasSelectedHiType(item)
      );
      if (activeConsentRequestId) {
        const activeRequest = patientConsentRequests.find(
          (item) => item?.consentRequestId === activeConsentRequestId
        );
        if (activeRequest) return activeRequest;
      }
      return newestSelectedRequests[0] || null;
    },
    [activeConsentRequestId, itemHasSelectedHiType, patientConsentRequests]
  );

  const latestUsableConsentId = useMemo(() => {
    const grantedRequests = newestFirst(patientConsentRequests).filter(
      (item) =>
        `${item?.status || ""}`.toUpperCase() === "GRANTED" &&
        itemHasSelectedHiType(item) &&
        Array.isArray(item?.consentArtefactIds) &&
        item.consentArtefactIds.length > 0
    );
    if (grantedRequests?.[0]?.consentArtefactIds?.[0]) {
      return grantedRequests[0].consentArtefactIds[0];
    }

    const grantedArtifacts = newestFirst(patientConsentArtifacts).filter((item) => {
      const isGranted = ["GRANTED", "ACTIVE"].includes(`${item?.status || ""}`.toUpperCase());
      const isSelectedType = itemHasSelectedHiType(item);
      const isCurrentFlow =
        !consentRequestedAt || !getItemTime(item) || getItemTime(item) >= consentRequestedAt - 30000;
      return isGranted && isSelectedType && isCurrentFlow;
    });

    const grantedRequest =
      currentConsentRequest &&
      `${currentConsentRequest?.status || ""}`.toUpperCase() === "GRANTED" &&
      itemHasSelectedHiType(currentConsentRequest) &&
      Array.isArray(currentConsentRequest?.consentArtefactIds) &&
      currentConsentRequest.consentArtefactIds.length > 0
        ? currentConsentRequest
        : null;
    if (grantedRequest?.consentArtefactIds?.[0]) {
      return grantedRequest.consentArtefactIds[0];
    }

    const usableArtifact = grantedArtifacts[0];
    return usableArtifact?.consentId || "";
  }, [
    consentRequestedAt,
    currentConsentRequest,
    itemHasSelectedHiType,
    patientConsentArtifacts,
    patientConsentRequests,
  ]);

  const effectiveConsentId = consentIdInput || latestUsableConsentId;
  const selectedConsentRequest = currentConsentRequest;
  const latestMatchingArtifact = useMemo(
    () =>
      newestFirst(patientConsentArtifacts).find(
        (item) => itemHasSelectedHiType(item) && isConsentGrantedStatus(item?.status)
      ) || null,
    [itemHasSelectedHiType, patientConsentArtifacts]
  );
  const currentConsentStatus = normalizeConsentStatus(
    selectedConsentRequest?.status || latestMatchingArtifact?.status
  );
  const consentStatusInfo = getConsentStatusInfo(
    currentConsentStatus,
    Boolean(selectedConsentRequest),
    Boolean(effectiveConsentId)
  );
  const canFetchHealthRecords =
    Boolean(patientAbhaAddress?.trim()) &&
    Boolean(effectiveConsentId) &&
    isConsentGrantedStatus(currentConsentStatus);
  const visibleHealthRecords =
    patientAbhaAddress && showCurrentRecords && isConsentGrantedStatus(currentConsentStatus)
      ? healthRecords || []
      : [];
  const hasReceivedRecords = visibleHealthRecords.length > 0;

  const ensureM3Ready = () => {
    if (!clinicId) {
      setLocalMessage("Clinic ID is missing. Please reload after clinic profile is loaded.");
      return false;
    }
    if (!authToken) {
      setLocalMessage("Login token is missing. Please login again.");
      return false;
    }
    return true;
  };

  const refreshM3Lists = useCallback(() => {
    if (!clinicId || !authToken) return;
    const patientId = patientAbhaAddress?.trim() || undefined;
    if (!patientId) {
      dispatch(clearShareAbhaDataM3PatientData());
      setLocalMessage("Patient ABHA address is not available. Link ABHA before requesting records.");
      setShowCurrentRecords(false);
      return;
    }

    dispatch(
      listConsentRequestsM3({
        clinicId,
        patientId,
        page: 1,
        limit: M3_LIST_LIMIT,
        token: authToken,
      })
    );

    dispatch(
      listConsentArtifactsM3({
        clinicId,
        patientId,
        page: 1,
        limit: M3_LIST_LIMIT,
        token: authToken,
      })
    );

    if (effectiveConsentId) {
      dispatch(
        listHealthRecordsM3({
          clinicId,
          consentId: effectiveConsentId,
          page: 1,
          limit: M3_LIST_LIMIT,
          token: authToken,
        })
      );
    } else {
      dispatch(clearShareAbhaDataM3HealthRecords());
    }
  }, [authToken, clinicId, dispatch, effectiveConsentId, patientAbhaAddress]);

  const handleLoadBridgeServices = () => {
    if (!ensureM3Ready()) return;
    setLocalMessage("");
    dispatch(getBridgeServicesM3({ clinicId, token: authToken })).then((action) => {
      const services = action?.payload?.result?.list?.services || [];
      const matchedService = action?.payload?.result?.list?.matchedService || null;
      const recommendedServiceId =
        action?.payload?.result?.list?.recommendedHiuServiceId || "";

      if (recommendedServiceId) {
        setHiuId(recommendedServiceId);
        return;
      }

      if (matchedService?.id) {
        setHiuId(matchedService.id);
        return;
      }

      if (!hiuId && Array.isArray(services) && services.length > 0 && services[0]?.id) {
        setHiuId(services[0].id);
      }
    });
  };

  const prepareM3Connection = async () => {
    if (!ensureM3Ready()) return "";
    setLocalMessage("Preparing ABDM connection...");
    await dispatch(setBridgeUrlM3({ clinicId, token: authToken }));
    const servicesAction = await dispatch(getBridgeServicesM3({ clinicId, token: authToken }));
    const services = servicesAction?.payload?.result?.list?.services || [];
    const matchedService = servicesAction?.payload?.result?.list?.matchedService || null;
    const recommendedServiceId =
      servicesAction?.payload?.result?.list?.recommendedHiuServiceId || "";
    const resolvedHiuId =
      recommendedServiceId ||
      matchedService?.id ||
      hiuId ||
      (Array.isArray(services) && services[0]?.id ? services[0].id : "");

    if (resolvedHiuId) {
      setHiuId(resolvedHiuId);
    }
    setLocalMessage("");
    return resolvedHiuId;
  };

  useEffect(() => {
    if (!clinicId || !authToken || !patientAbhaAddress?.trim()) return;
    handleLoadBridgeServices();
    refreshM3Lists();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clinicId, authToken, patientAbhaAddress]);

  const handleSetBridgeUrl = () => {
    if (!ensureM3Ready()) return;
    setLocalMessage("");
    dispatch(setBridgeUrlM3({ clinicId, token: authToken }));
  };

  const handleAssociateBridge = () => {
    if (!ensureM3Ready()) return;
    setLocalMessage("");
    dispatch(associateBridgeIdM3({ clinicId, token: authToken }));
  };

  const handleConsentInit = async () => {
    if (!ensureM3Ready()) return;
    if (!patientAbhaAddress?.trim()) {
      setLocalMessage("Patient ABHA address is required before init consent.");
      return;
    }
    if (!dateFrom || !dateTo) {
      setLocalMessage("Select both From and To dates before init consent.");
      return;
    }
    if (new Date(dateFrom) > new Date(dateTo)) {
      setLocalMessage("From date cannot be after To date.");
      return;
    }
    setLocalMessage("");
    setConsentIdInput("");
    setShowCurrentRecords(false);
    setActiveConsentRequestId("");
    setConsentRequestedAt(Date.now());
    dispatch(clearShareAbhaDataM3HealthRecords());
    const resolvedHiuId = await prepareM3Connection();

    dispatch(
      initiateConsentRequestM3({
        clinicId,
        hiuId: resolvedHiuId || hiuId || undefined,
        patientAbhaAddress: patientAbhaAddress.trim(),
        hiTypes: [hiType],
        dateRange: {
          from: toIsoStart(dateFrom),
          to: toIsoEnd(dateTo),
        },
        token: authToken,
      })
    ).then((action) => {
      const consentRequestId = findConsentRequestId(action?.payload);
      if (consentRequestId) {
        setActiveConsentRequestId(consentRequestId);
      }
      dispatch(clearShareAbhaDataM3HealthRecords());
      dispatch(
        listConsentRequestsM3({
          clinicId,
          patientId: patientAbhaAddress.trim(),
          page: 1,
          limit: M3_LIST_LIMIT,
          token: authToken,
        })
      );
      dispatch(
        listConsentArtifactsM3({
          clinicId,
          patientId: patientAbhaAddress.trim(),
          page: 1,
          limit: M3_LIST_LIMIT,
          token: authToken,
        })
      );
    });
  };

  const handleConsentFetch = () => {
    if (!ensureM3Ready()) return;
    if (isConsentBlockedStatus(currentConsentStatus)) {
      setShowCurrentRecords(false);
      dispatch(clearShareAbhaDataM3HealthRecords());
      setLocalMessage(getConsentBlockMessage(currentConsentStatus));
      return;
    }
    if (!effectiveConsentId) {
      setLocalMessage("Approve consent in the ABHA sandbox app, refresh lists, then fetch artifact.");
      return;
    }
    setLocalMessage("");

    dispatch(
      fetchConsentArtifactM3({
        clinicId,
        hiuId: hiuId || undefined,
        consentId: effectiveConsentId,
        token: authToken,
      })
    ).then(() => {
      refreshM3Lists();
    });
  };

  const handleHealthRequest = () => {
    if (!ensureM3Ready()) return;
    if (isConsentBlockedStatus(currentConsentStatus)) {
      setShowCurrentRecords(false);
      dispatch(clearShareAbhaDataM3HealthRecords());
      setLocalMessage(getConsentBlockMessage(currentConsentStatus));
      return;
    }
    if (!effectiveConsentId) {
      setLocalMessage("Consent ID is required before requesting health information.");
      return;
    }
    setLocalMessage("");

    dispatch(
      requestHealthInformationM3({
        clinicId,
        hiuId: hiuId || undefined,
        consentId: effectiveConsentId,
        dateRange: {
          from: toIsoStart(dateFrom),
          to: toIsoEnd(dateTo),
        },
        token: authToken,
      })
    ).then(() => {
      refreshM3Lists();
    });
  };

  const handleCheckPatientApproval = async () => {
    if (!ensureM3Ready()) return;
    if (selectedConsentRequest?.consentRequestId) {
      await dispatch(
        getConsentRequestStatusM3({
          clinicId,
          hiuId: hiuId || undefined,
          consentRequestId: selectedConsentRequest.consentRequestId,
          token: authToken,
        })
      );
    }
    refreshM3Lists();
  };

  const handleFetchHealthRecords = async () => {
    if (!ensureM3Ready()) return;
    const patientId = patientAbhaAddress?.trim();
    if (!patientId) {
      setLocalMessage("Patient ABHA address is required before requesting health information.");
      return;
    }

    const requestsAction = await dispatch(
      listConsentRequestsM3({
        clinicId,
        patientId,
        page: 1,
        limit: M3_LIST_LIMIT,
        token: authToken,
      })
    );
    const artifactsAction = await dispatch(
      listConsentArtifactsM3({
        clinicId,
        patientId,
        page: 1,
        limit: M3_LIST_LIMIT,
        token: authToken,
      })
    );
    const freshConsentRequests = requestsAction?.payload?.result?.list || [];
    const freshConsentArtifacts = artifactsAction?.payload?.result?.list || [];
    const freshSelectedRequests = newestFirst(freshConsentRequests).filter((item) =>
      itemHasSelectedHiType(item)
    );
    const freshSelectedRequest = activeConsentRequestId
      ? freshConsentRequests.find((item) => item?.consentRequestId === activeConsentRequestId) ||
        freshSelectedRequests[0]
      : freshSelectedRequests[0];
    const freshMatchingArtifact = newestFirst(freshConsentArtifacts).find((item) =>
      itemHasSelectedHiType(item)
    );
    const freshConsentStatus = normalizeConsentStatus(
      freshSelectedRequest?.status || freshMatchingArtifact?.status
    );

    if (isConsentBlockedStatus(freshConsentStatus)) {
      setShowCurrentRecords(false);
      dispatch(clearShareAbhaDataM3HealthRecords());
      setLocalMessage(getConsentBlockMessage(freshConsentStatus));
      return;
    }

    const freshGrantedRequest = newestFirst(freshConsentRequests).find(
      (item) =>
        `${item?.status || ""}`.toUpperCase() === "GRANTED" &&
        itemHasSelectedHiType(item) &&
        Array.isArray(item?.consentArtefactIds) &&
        item.consentArtefactIds.length > 0
    );
    const freshGrantedArtifact = newestFirst(freshConsentArtifacts).find(
      (item) =>
        ["GRANTED", "ACTIVE"].includes(`${item?.status || ""}`.toUpperCase()) &&
        itemHasSelectedHiType(item)
    );
    const resolvedConsentId =
      consentIdInput ||
      freshGrantedRequest?.consentArtefactIds?.[0] ||
      freshGrantedArtifact?.consentId ||
      effectiveConsentId;

    if (!resolvedConsentId || !isConsentGrantedStatus(freshConsentStatus || currentConsentStatus)) {
      setLocalMessage("Please ask the patient to approve consent, then refresh the status.");
      return;
    }
    setConsentIdInput(resolvedConsentId);
    setLocalMessage("Fetching consent artifact...");

    await dispatch(
      fetchConsentArtifactM3({
        clinicId,
        hiuId: hiuId || undefined,
        consentId: resolvedConsentId,
        token: authToken,
      })
    );

    let consentArtifactReady = false;
    for (let attempt = 0; attempt < 6; attempt += 1) {
      if (attempt > 0) {
        await sleep(2000);
      }
      const artifactPollAction = await dispatch(
        listConsentArtifactsM3({
          clinicId,
          patientId,
          page: 1,
          limit: M3_LIST_LIMIT,
          token: authToken,
        })
      );
      const artifactList = artifactPollAction?.payload?.result?.list || [];
      consentArtifactReady = artifactList.some(
        (item) =>
          item?.consentId === resolvedConsentId &&
          ["GRANTED", "ACTIVE"].includes(`${item?.status || ""}`.toUpperCase())
      );
      if (consentArtifactReady) break;
    }

    if (!consentArtifactReady) {
      setLocalMessage(
        "Consent artifact is still being fetched. Please wait a few seconds and click Get Health Records again."
      );
      return;
    }

    setLocalMessage("Requesting health information...");

    await dispatch(
      requestHealthInformationM3({
        clinicId,
        hiuId: hiuId || undefined,
        consentId: resolvedConsentId,
        dateRange: {
          from: toIsoStart(dateFrom),
          to: toIsoEnd(dateTo),
        },
        token: authToken,
      })
    );

    setShowCurrentRecords(true);
    setLocalMessage("Records request sent. Waiting for ABDM health information...");

    for (let attempt = 0; attempt < 6; attempt += 1) {
      if (attempt > 0) {
        await sleep(3000);
      }
      const recordsAction = await dispatch(
        listHealthRecordsM3({
          clinicId,
          consentId: resolvedConsentId,
          page: 1,
          limit: M3_LIST_LIMIT,
          token: authToken,
        })
      );
      const records = recordsAction?.payload?.result?.list || [];
      if (records.length > 0) {
        setLocalMessage("Health records fetched.");
        return;
      }
    }

    setLocalMessage(
      "Health information request sent, but records have not arrived yet. Please refresh after a few seconds."
    );
    refreshM3Lists();
  };

  const handleNotify = () => {
    if (!ensureM3Ready()) return;
    if (!notifyTransactionId?.trim()) {
      setLocalMessage("Transaction ID is required for manual notify.");
      return;
    }
    setLocalMessage("");

    dispatch(
      notifyHealthInformationM3({
        clinicId,
        transactionId: notifyTransactionId.trim(),
        sessionStatus: "RECEIVED",
        token: authToken,
      })
    );
  };

  return (
    <div className={styles.abdmM3Wrapper}>
      <div className={styles.abdmM3Header}>
        <div>
          <div className={styles.abdmM3Eyebrow}>ABDM Consent Based Access</div>
          <h5>Patient Health Records</h5>
          <p>Request patient consent and view shared records from ABDM linked facilities.</p>
        </div>
        <button className="btn btn-outline-primary btn-sm" onClick={handleCheckPatientApproval}>
          Refresh Status
        </button>
      </div>

      <div className={styles.abdmM3SummaryGrid}>
        <div className={styles.abdmM3SummaryItem}>
          <span>Patient ABHA</span>
          <strong>{patientAbhaAddress || "Not available"}</strong>
        </div>
        <div className={styles.abdmM3SummaryItem}>
          <span>Consent</span>
          <strong>{consentStatusInfo.summary}</strong>
        </div>
        <div className={styles.abdmM3SummaryItem}>
          <span>Records Received</span>
          <strong>{visibleHealthRecords.length}</strong>
        </div>
      </div>

      <div className={styles.abdmM3Panel}>
        <div className={styles.abdmM3PanelTitle}>Request Records</div>
        <div className="row g-3 align-items-end">
          <div className="col-md-4">
            <label className="form-label mb-1">Record Type</label>
          <select
            className="form-select"
            value={hiType}
            onChange={(e) => {
              setHiType(e.target.value);
              setConsentIdInput("");
              setShowCurrentRecords(false);
              setConsentRequestedAt(0);
              dispatch(clearShareAbhaDataM3HealthRecords());
            }}
          >
            {HI_TYPE_OPTIONS.map((item) => (
              <option key={item.value} value={item.value}>
                {item.label}
              </option>
            ))}
          </select>
          </div>
          <div className="col-md-3">
            <label className="form-label mb-1">From</label>
            <input
              type="date"
              className="form-control"
              value={dateFrom}
              max={dateTo || toDateInputValue(new Date())}
              onChange={(e) => {
                setDateFrom(e.target.value);
                setShowCurrentRecords(false);
                setConsentRequestedAt(0);
                dispatch(clearShareAbhaDataM3HealthRecords());
              }}
            />
          </div>
          <div className="col-md-3">
            <label className="form-label mb-1">To</label>
            <input
              type="date"
              className="form-control"
              value={dateTo}
              max={toDateInputValue(new Date())}
              onChange={(e) => {
                setDateTo(e.target.value);
                setShowCurrentRecords(false);
                setConsentRequestedAt(0);
                dispatch(clearShareAbhaDataM3HealthRecords());
              }}
            />
          </div>
          <div className="col-md-2">
            <button className="btn btn-primary w-100" onClick={handleConsentInit} disabled={loading}>
              Request Consent
            </button>
          </div>
        </div>
      </div>

      <div className={styles.abdmM3Steps}>
        <div className={styles.abdmM3Step}>
          <span>1</span>
          <div>
            <strong>Consent request sent</strong>
            <p>{consentStatusInfo.requestText}</p>
          </div>
        </div>
        <div className={styles.abdmM3Step}>
          <span>2</span>
          <div>
            <strong>Patient approval</strong>
            <p>{consentStatusInfo.approvalText}</p>
          </div>
        </div>
        <div className={styles.abdmM3Step}>
          <span>3</span>
          <div>
            <strong>Health records</strong>
            <p>{hasReceivedRecords ? "Records received" : consentStatusInfo.recordsText}</p>
          </div>
        </div>
      </div>

      <div className={styles.abdmM3ActionBar}>
        <button className="btn btn-outline-primary" onClick={handleCheckPatientApproval} disabled={loading}>
          Check Patient Approval
        </button>
        <button
          className="btn btn-success"
          onClick={handleFetchHealthRecords}
          disabled={loading || !canFetchHealthRecords}
        >
          Get Health Records
        </button>
      </div>

      {consentStatusInfo.notice && (
        <div className={`alert ${consentStatusInfo.alertClass} py-2 px-3`} role="alert">
          {consentStatusInfo.notice}
        </div>
      )}

      {(loading || error || localMessage || lastMessage) && (
        <div
          className={`alert ${error ? "alert-danger" : "alert-info"} py-2 px-3`}
          role="alert"
        >
          {error || localMessage || lastMessage || (loading ? "Processing..." : "")}
        </div>
      )}

      <div className={styles.abdmM3RecordsHeader}>
        <h6>Shared Health Records</h6>
        <span>{visibleHealthRecords.length} record(s)</span>
      </div>
      <div className={styles.abdmM3RecordGrid}>
        {visibleHealthRecords.length === 0 && (
          <div className={styles.abdmM3EmptyState}>
            {patientAbhaAddress
              ? "Patient-approved records will appear here after the ABDM consent flow is completed."
              : "This patient does not have an ABHA address linked, so M3 records cannot be requested."}
          </div>
        )}
        {visibleHealthRecords.map((item) => (
          <div key={item?._id} className={styles.abdmM3RecordCard}>
            <div className={styles.abdmM3RecordTop}>
              <strong>{formatHiType(item?.hiType)}</strong>
              <span className={`${styles.abdmM3StatusBadge} ${getStatusClass(item?.status)}`}>
                {item?.status || "Received"}
              </span>
            </div>
            <div className={styles.abdmM3RecordMeta}>
              <span>Received</span>
              <strong>{formatDateTime(item?.createdAt || item?.updatedAt)}</strong>
            </div>
            <button
              type="button"
              className="btn btn-link btn-sm p-0 mt-2"
              onClick={() =>
                dispatch(
                  getHealthRecordByIdM3({
                    clinicId,
                    recordId: item?._id,
                    token: authToken,
                  })
                )
              }
            >
              View record details
            </button>
          </div>
        ))}
      </div>

      {healthRecordDetail && (
        <div className={styles.abdmM3Panel}>
          <div className={styles.abdmM3PanelTitle}>Selected Record Details</div>
          <pre className={styles.abdmM3Payload}>
            {JSON.stringify(healthRecordDetail?.parsedContent || healthRecordDetail, null, 2)}
          </pre>
        </div>
      )}

      <details className={styles.abdmM3TechnicalDetails}>
        <summary>Integration details</summary>
        <div className="row g-2 mb-3 mt-2">
          <div className="col-md-4">
            <label className="form-label mb-1">Patient ABHA Address</label>
            <input
              className="form-control form-control-sm"
              value={patientAbhaAddress}
              onChange={(e) => setPatientAbhaAddress(e.target.value)}
              placeholder="patient@sbx"
            />
          </div>
          <div className="col-md-4">
            <label className="form-label mb-1">HIU ID</label>
            <input
              className="form-control form-control-sm"
              value={hiuId}
              onChange={(e) => setHiuId(e.target.value)}
              placeholder="Use clinic ABHA facility if empty"
            />
          </div>
          <div className="col-md-4">
            <label className="form-label mb-1">Consent ID</label>
            <input
              className="form-control form-control-sm"
              value={consentIdInput}
              onChange={(e) => setConsentIdInput(e.target.value)}
              placeholder={latestUsableConsentId || "Latest granted consent"}
            />
          </div>
        </div>

        <div className="d-flex flex-wrap gap-2 mb-3">
          <button className="btn btn-outline-primary btn-sm" onClick={handleSetBridgeUrl}>
            Set Bridge URL
          </button>
          <button className="btn btn-outline-primary btn-sm" onClick={handleLoadBridgeServices}>
            Check Linked Services
          </button>
          <button className="btn btn-outline-primary btn-sm" onClick={handleAssociateBridge}>
            Associate Bridge
          </button>
          <button className="btn btn-outline-success btn-sm" onClick={handleConsentFetch}>
            Fetch Consent Artifact
          </button>
          <button className="btn btn-success btn-sm" onClick={handleHealthRequest}>
            Request Health Info
          </button>
        </div>

        <div className="row g-2 align-items-end mb-3">
          <div className="col-md-6">
            <label className="form-label mb-1">Transaction ID (manual notify)</label>
          <input
            className="form-control form-control-sm"
            value={notifyTransactionId}
            onChange={(e) => setNotifyTransactionId(e.target.value)}
            placeholder="Only needed for manual re-notify"
          />
          </div>
          <div className="col-md-6">
            <button className="btn btn-outline-warning btn-sm" onClick={handleNotify}>
              Notify Health Information
            </button>
          </div>
        </div>

        <div className="border rounded p-2 mb-3">
          <div className="small mb-1">
            <strong>Bridge ID:</strong> {bridgeInfo?.id || "-"}
          </div>
          <div className="small mb-2">
            <strong>Matched Service:</strong> {bridgeServiceMatched?.id || "-"}
            {bridgeServiceMatched?.name ? ` (${bridgeServiceMatched.name})` : ""}
          </div>
          <div className="small mb-2">
            <strong>Recommended HIU Service ID:</strong> {recommendedHiuServiceId || "-"}
          </div>
          <div className="d-flex flex-wrap gap-2">
            {(bridgeServices || []).slice(0, 10).map((service) => (
              <button
                key={service?.id}
                className="btn btn-outline-secondary btn-sm"
                onClick={() => setHiuId(service?.id || "")}
              >
                {service?.id}
              </button>
            ))}
          </div>
        </div>

        <div className="row g-3">
        <div className="col-lg-4">
          <h6 className="mb-2">Consent Requests</h6>
          <div className="border rounded p-2" style={{ maxHeight: 220, overflowY: "auto" }}>
            {(consentRequests || []).length === 0 && (
              <div className="text-muted small">No consent requests.</div>
            )}
            {(consentRequests || []).map((item) => (
              <div key={item?._id} className="small border-bottom pb-2 mb-2">
                <div>
                  <strong>Status:</strong> {item?.status || "-"}
                </div>
                <div>
                  <strong>Consent Request ID:</strong> {item?.consentRequestId || "-"}
                </div>
                <div>
                  <strong>Consent IDs:</strong>{" "}
                  {Array.isArray(item?.consentArtefactIds) && item.consentArtefactIds.length > 0
                    ? item.consentArtefactIds.join(", ")
                    : "-"}
                </div>
                <div>
                  <strong>HI Types:</strong>{" "}
                  {Array.isArray(item?.hiTypes) && item.hiTypes.length > 0
                    ? item.hiTypes.map(formatHiType).join(", ")
                    : "-"}
                </div>
                <div>
                  <strong>Created:</strong> {formatDateTime(item?.createdAt)}
                </div>
                <div>
                  <strong>Patient:</strong> {item?.patientId || "-"}
                </div>
                <div className="d-flex flex-wrap gap-2 mt-1">
                  {item?.consentRequestId && (
                    <button
                      type="button"
                      className="btn btn-link btn-sm p-0"
                      onClick={() => {
                        setActiveConsentRequestId(item.consentRequestId);
                        setConsentIdInput("");
                        setShowCurrentRecords(false);
                        dispatch(clearShareAbhaDataM3HealthRecords());
                        dispatch(
                          getConsentRequestStatusM3({
                            clinicId,
                            hiuId: hiuId || undefined,
                            consentRequestId: item.consentRequestId,
                            token: authToken,
                          })
                        ).then(refreshM3Lists);
                      }}
                    >
                      Check status
                    </button>
                  )}
                  {Array.isArray(item?.consentArtefactIds) &&
                    item.consentArtefactIds.map((consentId) => (
                      <button
                        key={consentId}
                        type="button"
                        className="btn btn-link btn-sm p-0"
                        onClick={() => setConsentIdInput(consentId)}
                      >
                        Use {consentId}
                      </button>
                    ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="col-lg-4">
          <h6 className="mb-2">Consent Artifacts</h6>
          <div className="border rounded p-2" style={{ maxHeight: 220, overflowY: "auto" }}>
            {(consentArtifacts || []).length === 0 && (
              <div className="text-muted small">No consent artifacts.</div>
            )}
            {(consentArtifacts || []).map((item) => (
              <div key={item?._id} className="small border-bottom pb-2 mb-2">
                <div>
                  <strong>Status:</strong> {item?.status || "-"}
                </div>
                <div>
                  <strong>Consent ID:</strong> {item?.consentId || "-"}
                </div>
                <div>
                  <strong>HIP:</strong> {item?.hipId || "-"}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="col-lg-4">
          <h6 className="mb-2">Health Records</h6>
          <div className="border rounded p-2" style={{ maxHeight: 220, overflowY: "auto" }}>
            {visibleHealthRecords.length === 0 && (
              <div className="text-muted small">No records received yet.</div>
            )}
            {visibleHealthRecords.map((item) => (
              <div key={item?._id} className="small border-bottom pb-2 mb-2">
                <div>
                  <strong>Type:</strong> {item?.hiType || "-"}
                </div>
                <div>
                  <strong>Status:</strong> {item?.status || "-"}
                </div>
                <div>
                  <strong>Txn:</strong> {item?.transactionId || "-"}
                </div>
                <button
                  type="button"
                  className="btn btn-link btn-sm p-0"
                  onClick={() =>
                    dispatch(
                      getHealthRecordByIdM3({
                        clinicId,
                        recordId: item?._id,
                        token: authToken,
                      })
                    )
                  }
                >
                  View payload
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
      </details>
    </div>
  );
};

export default AbhaM3Section;
